import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const StripeCardForm = ({ onPaymentSuccess, onPaymentError, shippingInfo }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [amount, setAmount] = useState(0);
  
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const { loadCartItems } = useCart();

  // Get payment intent when component mounts
  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/stripe/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to create payment intent');
        }

        const data = await response.json();
        setClientSecret(data.clientSecret);
        setAmount(data.amount);
      } catch (err) {
        console.error('Error creating payment intent:', err);
        setError('Erreur lors de la création du paiement');
      }
    };

    createPaymentIntent();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Confirm card payment
      const { error: paymentError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: user?.name || '',
            email: user?.email || '',
          },
        },
      });

      if (paymentError) {
        setError(paymentError.message || 'Erreur de paiement');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Confirm payment on our server
        const token = localStorage.getItem('token');
        const confirmResponse = await fetch('/api/stripe/confirm-payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id,
            shippingInfo
          })
        });

        if (!confirmResponse.ok) {
          throw new Error('Failed to confirm payment');
        }

        const confirmData = await confirmResponse.json();
        
        // Reload cart to reflect changes
        await loadCartItems();
        
        // Call success callback
        onPaymentSuccess({
          orderId: confirmData.orderId,
          orderNumber: confirmData.orderNumber,
          amount: confirmData.amount,
          paymentMethod: 'stripe'
        });

      } else {
        setError('Le paiement n\'a pas été complété');
      }

    } catch (err) {
      console.error('Payment error:', err);
      setError('Erreur lors du traitement du paiement');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Préparation du paiement...</span>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Paiement par carte bancaire
          </h3>
          <p className="text-sm text-gray-600">
            Montant total: <span className="font-semibold">{amount.toFixed(2)} €</span>
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Informations de la carte
            </label>
            <div className="border border-gray-300 rounded-md p-3 bg-gray-50">
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!stripe || isProcessing}
            className={`w-full py-3 px-4 rounded-md font-medium text-white transition-colors ${
              !stripe || isProcessing
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isProcessing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Traitement...
              </div>
            ) : (
              `Payer ${amount.toFixed(2)} €`
            )}
          </button>
        </form>

        <div className="mt-4 text-xs text-gray-500 text-center">
          <p>Vos informations de paiement sont sécurisées par Stripe</p>
          <div className="flex items-center justify-center mt-2 space-x-2">
            <img src="https://stripe.com/img/v3/home/twitter.png" alt="Stripe" className="h-4" />
            <span>Paiement sécurisé</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeCardForm;
